/**
 * Restaurant-managed delivery pricing only. Platform-delivery pricing is a future, separate
 * module (`platformDeliveryPricing.ts` — not in MVP).
 *
 * A delivery tier kicks in for orders whose net subtotal is `>= minOrderValueMinorUnits`.
 * Tiers are applied highest-threshold-first to find the first match.
 */

export interface RestaurantDeliveryTier {
  readonly minOrderValueMinorUnits: bigint;
  readonly deliveryFeeMinorUnits: bigint;
}

export class NoApplicableDeliveryTierError extends Error {
  constructor(public readonly subtotalMinorUnits: bigint) {
    super(`No applicable delivery tier for subtotal=${subtotalMinorUnits}`);
    this.name = 'NoApplicableDeliveryTierError';
  }
}

export function calculateRestaurantDeliveryFee(
  tiers: readonly RestaurantDeliveryTier[],
  subtotalMinorUnits: bigint,
): bigint {
  if (tiers.length === 0) {
    throw new NoApplicableDeliveryTierError(subtotalMinorUnits);
  }
  const sorted = [...tiers].sort((a, b) =>
    a.minOrderValueMinorUnits < b.minOrderValueMinorUnits ? 1 : -1,
  );
  for (const tier of sorted) {
    if (subtotalMinorUnits >= tier.minOrderValueMinorUnits) {
      return tier.deliveryFeeMinorUnits;
    }
  }
  throw new NoApplicableDeliveryTierError(subtotalMinorUnits);
}
